<template>
    <div>
        <div class="top-header">
            <h1>Bedrifter</h1>
        </div>
        <div class="page__content page__content--toplist">
            <form @submit.prevent id="toplist-filter">
                <div class="select select--competitions">
                    <select v-model="competition">
                        <option v-for="c in filters.competitions" :key="c.id" :value="c.id">{{ c.name }}</option>
                    </select>
                </div>
                <input type="text" class="searchfield" v-model="query" placeholder="Søk etter navn">
            </form>

            <div id="toplist-loading" v-if="initialLoading"><div class="toplist__spinner"></div></div>
            <template v-else>
                <div class="toplist-back"><a href="#" class="action" @click.prevent="$emit('back')">&laquo; Tilbake</a></div>
                <div class="toplist__content" :class="{ 'toplist__content--loading': loading }">
                    <div class="toplist__loading-overlay" v-if="loading"><div class="toplist__spinner"></div></div>
                    <div class="table__summary-wrapper">
                        <div class="table__summary">
                            <div class="table__summary-item hero__stat">
                                <div class="hero__statcount">{{ regFormat(summary.user_count) }}</div>
                                <div class="hero__stat-text">Bedriftsdeltagere</div>
                            </div>
                            <div class="table__summary-item hero__stat">
                                <div class="hero__statcount">{{ regFormat(summary.reg_count) }}</div>
                                <div class="hero__stat-text">Registreringer av ansatte</div>
                            </div>
                        </div>
                    </div>
                    <div class="table" v-if="toplist.length">
                        <table>
                            <thead>
                                <tr>
                                    <th class="table__header--ranking">#</th>
                                    <th class="table__header--name">Bedrift</th>
                                    <th style="text-align: right;"><a href="#" class="table__sorting" :class="{ 'table__sorting--active': order === 'registrations' }" @click.prevent="order = 'registrations'">Registreringer<i v-if="order === 'registrations'" class="pi pi-sort-amount-down"></i></a></th>
                                    <th style="text-align: right;">Aktive ansatte</th>
                                    <th style="text-align: right;"><a href="#" class="table__sorting" :class="{ 'table__sorting--active': order === 'targets' }" @click.prevent="order = 'targets'">Turmål&nbsp;/ ansatt<i v-if="order === 'targets'" class="pi pi-sort-amount-down"></i></a></th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(entry, index) in toplist" :key="entry.company_id"
                                    class="table__entry"
                                    :class="{ 'table__entry--first': index === 0, 'table__entry--last': index === toplist.length - 1, 'table__entry--user': isUserCompany(entry.company_id) }">
                                    <td class="table__ranking table__num">{{ entry.rank }}</td>
                                    <td class="table__name">{{ entry.name }}</td>
                                    <td class="table__reg table__num" :class="{ 'table__num--current': order === 'registrations' }">{{ entry.registrations }}</td>
                                    <td class="table__reg table__num">{{ entry.employees }}</td>
                                    <td class="table__reg table__num" :class="{ 'table__num--current': order === 'targets' }">{{ entry.reg_pr_employee.toFixed(1) }}</td>
                                </tr>
                                <template v-if="unlistedUserEntries.length">
                                    <tr>
                                        <td></td>
                                        <td style="font-size: 0.1em;">&nbsp;</td>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                    <tr v-for="entry in unlistedUserEntries" :key="entry.company_id" class="table__entry--user">
                                        <td class="table__ranking table__num">{{ entry.rank }}</td>
                                        <td class="table__name"><a href="#" class="table__secondary-link" @click.prevent="goToCompanyPage(entry)">{{ entry.name }}</a></td>
                                        <td class="table__reg table__num">{{ entry.registrations }}</td>
                                        <td class="table__reg table__num">{{ entry.employees }}</td>
                                        <td class="table__reg table__num">{{ entry.reg_pr_employee.toFixed(1) }}</td>
                                    </tr>
                                </template>
                            </tbody>
                        </table>
                        <ToplistPager :current-page="pagination.current_page" :last-page="pagination.last_page" @change="goToPage" />
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { regFormat } from '../config/format.js';
import ToplistPager from './ToplistPager.vue';

const props = defineProps({ competitionId: { type: Number, default: null } });
defineEmits(['back']);

const params = new URLSearchParams(window.location.search);

const competition = ref(params.get('competition') ? Number(params.get('competition')) : (props.competitionId || ''));
const query = ref(params.get('q') || '');
const order = ref(params.get('order') === 'registrations' ? 'registrations' : 'targets');
const page = ref(params.get('page') ? Number(params.get('page')) : 1);

const loading = ref(true);
const initialLoading = ref(true);
const toplist = ref([]);
const pagination = ref({ current_page: 1, last_page: 1, total: 0, per_page: 25 });
const summary = ref({ reg_count: 0, user_count: 0 });
const filters = ref({ competitions: [] });
const userEntries = ref([]);

function isUserCompany(companyId) {
    return userEntries.value.some((entry) => entry.company_id === companyId);
}

// A company already visible on the current page doesn't need its own "your
// company" row (it's already highlighted in place via isUserCompany() above)
// - someone can belong to more than one company, unlike the person
// leaderboard's single userEntry.
const unlistedUserEntries = computed(() =>
    userEntries.value.filter((entry) => !toplist.value.some((row) => row.company_id === entry.company_id))
);

let requestToken = 0;
let userEntriesToken = 0;
let searchDebounce = null;

async function load() {
    const token = ++requestToken;
    loading.value = true;

    const search = new URLSearchParams();
    if (competition.value) search.set('competition', competition.value);
    if (query.value) search.set('q', query.value);
    search.set('order', order.value);
    search.set('page', page.value);
    search.set('type', 'companies');
    history.replaceState({}, '', '/topplister?' + search.toString());

    try {
        const response = await fetch('/xhr/frontend/toplists/competition/companies?' + search.toString(), {
            headers: { Accept: 'application/json' },
        });
        if (response.ok && token === requestToken) {
            const data = await response.json();
            toplist.value = data.toplist?.data || [];
            pagination.value = {
                current_page: data.toplist?.current_page || 1,
                last_page: data.toplist?.last_page || 1,
                total: data.toplist?.total || 0,
                per_page: data.toplist?.per_page || 25,
            };
            summary.value = data.summary || summary.value;
            filters.value = { competitions: data.competitions || [] };
        }
    } catch (e) {
        // Leave the previous state in place.
    } finally {
        if (token === requestToken) {
            loading.value = false;
            initialLoading.value = false;
        }
    }
}

// Split from load() so the (person-independent) main list request stays
// cacheable - "your companies" doesn't depend on page or search query, only
// on $order (which changes the ranking itself), so it's fetched separately.
async function loadUserEntries() {
    const token = ++userEntriesToken;

    const search = new URLSearchParams();
    if (competition.value) search.set('competition', competition.value);
    search.set('order', order.value);

    try {
        const response = await fetch('/xhr/frontend/toplists/competition/companies/me?' + search.toString(), {
            headers: { Accept: 'application/json' },
        });
        if (response.ok && token === userEntriesToken) {
            const data = await response.json();
            userEntries.value = data.user_entries || [];
        }
    } catch (e) {
        // Leave the previous state in place.
    }
}

function goToPage(next) {
    page.value = next;
}

// entry.position (a gapless 1-indexed row number, unlike entry.rank which
// ties can share and which can therefore span a page boundary) is only
// meaningful against the unfiltered list companyToplist() paginates when no
// search is active - clearing an active query re-triggers its own watcher
// below, so this waits a tick for that debounce to get (re)scheduled and
// then cancels it, rather than letting it fire later and reset the page
// this just navigated to.
async function goToCompanyPage(entry) {
    if (query.value) {
        query.value = '';
        await nextTick();
        clearTimeout(searchDebounce);
    }
    page.value = Math.ceil(entry.position / pagination.value.per_page);
    load();
}

watch([competition, order], () => {
    page.value = 1;
    load();
    loadUserEntries();
});

watch(page, load);

watch(query, () => {
    clearTimeout(searchDebounce);
    searchDebounce = setTimeout(() => {
        page.value = 1;
        load();
    }, 300);
});

onMounted(() => {
    load();
    loadUserEntries();
});
</script>
